import { type ReactNode, useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ACCENT_CLS,
  type AccentKey,
} from "@/components/parent/shared/parentPrimitives";
import type { ParentAnalyticsResult, QuizRow } from "@/hooks/useParentAnalytics";
import type { PipelineRow, ScorecardRow } from "@/lib/parent/commandMetrics";
import type { ParentPriority } from "@/lib/parent/currentPriority";
import { Layers, Gauge, Radar, BookOpen, ClipboardCheck, Clock, ExternalLink } from "lucide-react";

/* -----------------------------------------------------------
 * Shared drilldown shell
 * ----------------------------------------------------------- */

function DrilldownShell({
  trigger,
  eyebrow,
  title,
  subtitle,
  accent = "cyan",
  icon: Icon,
  children,
}: {
  trigger: ReactNode;
  eyebrow: string;
  title: string;
  subtitle?: string;
  accent?: AccentKey;
  icon?: React.ComponentType<{ className?: string }>;
  children: ReactNode;
}) {
  const cls = ACCENT_CLS[accent];
  return (
    <Sheet>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full max-w-full overflow-y-auto border-l border-white/[0.06] bg-[linear-gradient(180deg,hsl(var(--card)/0.95)_0%,hsl(var(--background)/0.98)_100%)] p-0 backdrop-blur-2xl sm:max-w-xl"
      >
        <div className={cn("pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent to-transparent", cls.hairline)} />
        <SheetHeader className="space-y-3 border-b border-white/[0.06] px-6 pb-5 pt-6 text-left">
          <div className="flex items-center gap-3">
            {Icon && (
              <span className={cn("flex h-9 w-9 items-center justify-center rounded-xl ring-1", cls.chip)}>
                <Icon className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0">
              <p className={cn("text-[10.5px] font-medium uppercase tracking-[0.22em]", cls.text)}>{eyebrow}</p>
              <SheetTitle className="font-heading text-[20px] font-semibold leading-tight tracking-tight">{title}</SheetTitle>
            </div>
          </div>
          {subtitle && (
            <SheetDescription className="text-[12.5px] leading-relaxed">{subtitle}</SheetDescription>
          )}
        </SheetHeader>
        <div className="space-y-5 px-6 pb-10 pt-5">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

function StatRow({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-white/[0.04] py-2 last:border-b-0">
      <div className="min-w-0">
        <p className="text-[11.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground/85">{label}</p>
        {hint && <p className="mt-0.5 text-[11px] text-muted-foreground/75">{hint}</p>}
      </div>
      <p className="shrink-0 font-heading text-[15px] font-semibold tracking-tight text-foreground">{value}</p>
    </div>
  );
}

function SectionTitle({ children, accent = "neutral" }: { children: ReactNode; accent?: AccentKey }) {
  const cls = ACCENT_CLS[accent];
  return (
    <p className={cn("text-[10.5px] font-medium uppercase tracking-[0.22em]", cls.text)}>{children}</p>
  );
}

function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border border-dashed border-white/[0.08] bg-white/[0.015] px-3 py-3 text-[12px] text-muted-foreground/85">
      {children}
    </p>
  );
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function relWhen(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const m = Math.round(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* -----------------------------------------------------------
 * Module-matcher helpers (beta-scoped activity slicing)
 * ----------------------------------------------------------- */

const MODULE_MATCHERS: Record<string, string[]> = {
  budget: ["money", "budget", "saving", "spending", "income", "cash", "needs", "want"],
  credit: ["credit", "debt", "loan", "interest", "card", "borrow", "minimum"],
  scam: ["scam", "fraud", "phishing", "urgency", "too good"],
  decision: ["decision", "tradeoff", "trade-off", "choice", "opportunity"],
  money: ["money", "budget", "saving", "spending", "income", "cash"],
};

function lessonsForModule(a: ParentAnalyticsResult, key: string) {
  const m = MODULE_MATCHERS[key] ?? [];
  return a.lessonActivity
    .filter((l) => {
      const hay = `${l.lessonName ?? ""} ${l.topic ?? ""}`.toLowerCase();
      return m.some((kw) => hay.includes(kw));
    })
    .slice()
    .sort((x, y) => (x.date > y.date ? -1 : 1));
}

function quizzesForModule(a: ParentAnalyticsResult, key: string): QuizRow[] {
  const m = MODULE_MATCHERS[key] ?? [];
  return a.quizAttempts
    .filter((q) => {
      const hay = `${q.quizTitle ?? ""} ${q.topic ?? ""}`.toLowerCase();
      return m.some((kw) => hay.includes(kw));
    })
    .slice()
    .sort((x, y) => (x.completedAt > y.completedAt ? -1 : 1));
}

/* -----------------------------------------------------------
 * 1. Module Progress drilldown
 * ----------------------------------------------------------- */

export function ModuleProgressDrilldown({
  row,
  analytics,
  trigger,
}: {
  row: PipelineRow;
  analytics: ParentAnalyticsResult;
  trigger: ReactNode;
}) {
  const stats = useMemo(() => {
    if (row.advanced) return null;
    const key = row.id as keyof ParentAnalyticsResult["betaModuleStats"];
    return analytics.betaModuleStats[key] ?? null;
  }, [row, analytics.betaModuleStats]);

  const recentLessons = useMemo(() => lessonsForModule(analytics, row.id).slice(0, 8), [analytics, row.id]);
  const recentQuizzes = useMemo(() => quizzesForModule(analytics, row.id).slice(0, 8), [analytics, row.id]);

  const readinessAccent: AccentKey = row.advanced
    ? "amber"
    : row.readiness >= 70
      ? "emerald"
      : row.readiness >= 35
        ? "cyan"
        : "neutral";

  return (
    <DrilldownShell
      trigger={trigger}
      eyebrow="Module progress"
      title={row.name}
      subtitle={row.evidence}
      accent={readinessAccent}
      icon={Layers}
    >
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
        <SectionTitle accent={readinessAccent}>Path stats</SectionTitle>
        <div className="mt-2">
          <StatRow
            label="Readiness"
            value={row.advanced ? "—" : `${row.readiness}%`}
            hint={`Status · ${row.status}`}
          />
          {stats && (
            <>
              <StatRow
                label="Lessons completed"
                value={`${stats.lessonsCompleted} / ${stats.lessonsTotal}`}
              />
              <StatRow
                label="Quiz attempts"
                value={stats.quizCount}
                hint={stats.quizCount > 0 ? `Average ${Math.round(stats.averageQuizScore)}%` : "No quiz data yet"}
              />
              <StatRow
                label="Module completed"
                value={stats.moduleCompleted ? "Yes" : "Not yet"}
              />
              <StatRow
                label="Last activity"
                value={stats.lastActivityAt ? relWhen(stats.lastActivityAt) : "—"}
                hint={stats.lastActivityAt ? formatStamp(stats.lastActivityAt) : undefined}
              />
            </>
          )}
        </div>
      </section>

      <section>
        <SectionTitle accent="cyan">Recent lessons</SectionTitle>
        <div className="mt-2 space-y-1.5">
          {recentLessons.length === 0 ? (
            <EmptyNote>No lessons in this module have been opened yet.</EmptyNote>
          ) : (
            recentLessons.map((l, i) => (
              <div key={`${l.date}-${i}`} className="flex items-start gap-2.5 rounded-xl border border-white/[0.04] bg-background/30 px-3 py-2">
                <BookOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-300" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-medium text-foreground">{l.lessonName}</p>
                  {l.topic && <p className="truncate text-[11px] text-muted-foreground/85">{l.topic}</p>}
                </div>
                <p className="shrink-0 text-[10.5px] text-muted-foreground/80" title={formatStamp(l.date)}>
                  {relWhen(l.date)}
                </p>
              </div>
            ))
          )}
        </div>
      </section>

      <section>
        <SectionTitle accent="emerald">Recent quiz attempts</SectionTitle>
        <div className="mt-2 space-y-1.5">
          {recentQuizzes.length === 0 ? (
            <EmptyNote>No quiz attempts in this module yet.</EmptyNote>
          ) : (
            recentQuizzes.map((q) => (
              <div key={q.id} className="flex items-center gap-2.5 rounded-xl border border-white/[0.04] bg-background/30 px-3 py-2">
                <ClipboardCheck className={cn("h-3.5 w-3.5 shrink-0", q.passed ? "text-emerald-300" : "text-amber-300")} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-medium text-foreground">{q.quizTitle}</p>
                  <p className="truncate text-[11px] text-muted-foreground/85">
                    Attempt {q.attemptNumber} · {q.passed ? "Passed" : "Did not pass"}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className={cn("font-heading text-[13px] font-semibold", q.passed ? "text-emerald-300" : "text-amber-200")}>{q.score}%</p>
                  <p className="text-[10.5px] text-muted-foreground/80" title={formatStamp(q.completedAt)}>{relWhen(q.completedAt)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </DrilldownShell>
  );
}

/* -----------------------------------------------------------
 * 2. Focus area (Readiness scorecard) drilldown
 * ----------------------------------------------------------- */

export function FocusAreaDrilldown({
  row,
  analytics,
  trigger,
}: {
  row: ScorecardRow;
  analytics: ParentAnalyticsResult;
  trigger: ReactNode;
}) {
  const moduleKey = row.id === "money" ? "budget" : row.id; // map scorecard → beta module
  const moduleStats = useMemo(() => {
    if (moduleKey === "budget" || moduleKey === "credit" || moduleKey === "scam") {
      return analytics.betaModuleStats[moduleKey];
    }
    return null;
  }, [analytics.betaModuleStats, moduleKey]);

  const matchedTopics = useMemo(() => {
    const m = MODULE_MATCHERS[row.id] ?? MODULE_MATCHERS[moduleKey] ?? [];
    return analytics.topicMastery
      .filter((t) => {
        const lo = (t.topic || "").toLowerCase();
        return m.some((kw) => lo.includes(kw));
      })
      .slice()
      .sort((a, b) => b.masteryScore - a.masteryScore);
  }, [analytics.topicMastery, row.id, moduleKey]);

  const recentQuizzes = useMemo(
    () => quizzesForModule(analytics, row.id === "decision" ? "decision" : moduleKey).slice(0, 6),
    [analytics, row.id, moduleKey],
  );

  const accent: AccentKey =
    row.percent >= 70 ? "emerald" : row.percent >= 35 ? "cyan" : row.sampleSize > 0 ? "amber" : "neutral";

  return (
    <DrilldownShell
      trigger={trigger}
      eyebrow="Focus area"
      title={row.label}
      subtitle={row.whatItMeasures}
      accent={accent}
      icon={Gauge}
    >
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
        <SectionTitle accent={accent}>Signal</SectionTitle>
        <div className="mt-2">
          <StatRow label="Readiness" value={`${row.percent}%`} hint={`Signal · ${row.signal}`} />
          <StatRow label="Sample size" value={row.sampleSize} hint="Lessons + quizzes counted toward this signal" />
          {moduleStats && (
            <>
              <StatRow label="Lessons completed" value={`${moduleStats.lessonsCompleted} / ${moduleStats.lessonsTotal}`} />
              <StatRow
                label="Quiz average"
                value={moduleStats.quizCount > 0 ? `${Math.round(moduleStats.averageQuizScore)}%` : "—"}
                hint={moduleStats.quizCount > 0 ? `${moduleStats.quizCount} attempt${moduleStats.quizCount === 1 ? "" : "s"}` : "No quizzes yet"}
              />
              <StatRow
                label="Last activity"
                value={moduleStats.lastActivityAt ? relWhen(moduleStats.lastActivityAt) : "—"}
                hint={moduleStats.lastActivityAt ? formatStamp(moduleStats.lastActivityAt) : undefined}
              />
            </>
          )}
        </div>
      </section>

      <section>
        <SectionTitle accent="cyan">Underlying topics</SectionTitle>
        <div className="mt-2 space-y-1.5">
          {matchedTopics.length === 0 ? (
            <EmptyNote>No topic-level data matches this focus area yet.</EmptyNote>
          ) : (
            matchedTopics.slice(0, 8).map((t) => (
              <div key={t.topic} className="flex items-center justify-between gap-2 rounded-xl border border-white/[0.04] bg-background/30 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-[12.5px] font-medium text-foreground">{t.topic}</p>
                  <p className="text-[11px] text-muted-foreground/85">
                    {t.lessonsCompleted} lesson{t.lessonsCompleted === 1 ? "" : "s"} · {t.quizCount} quiz{t.quizCount === 1 ? "" : "zes"}
                    {t.quizCount > 0 ? ` · ${Math.round(t.averageQuizScore)}% avg` : ""}
                  </p>
                </div>
                <p className="font-heading text-[13px] font-semibold text-foreground">{Math.round(t.masteryScore)}%</p>
              </div>
            ))
          )}
        </div>
      </section>

      <section>
        <SectionTitle accent="emerald">Recent quizzes in this area</SectionTitle>
        <div className="mt-2 space-y-1.5">
          {recentQuizzes.length === 0 ? (
            <EmptyNote>No quiz attempts mapped to this focus area yet.</EmptyNote>
          ) : (
            recentQuizzes.map((q) => (
              <div key={q.id} className="flex items-center gap-2.5 rounded-xl border border-white/[0.04] bg-background/30 px-3 py-2">
                <ClipboardCheck className={cn("h-3.5 w-3.5 shrink-0", q.passed ? "text-emerald-300" : "text-amber-300")} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-medium text-foreground">{q.quizTitle}</p>
                  <p className="truncate text-[11px] text-muted-foreground/85">Attempt {q.attemptNumber}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className={cn("font-heading text-[13px] font-semibold", q.passed ? "text-emerald-300" : "text-amber-200")}>{q.score}%</p>
                  <p className="text-[10.5px] text-muted-foreground/80" title={formatStamp(q.completedAt)}>{relWhen(q.completedAt)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <p className="rounded-xl border border-white/[0.05] bg-white/[0.02] px-3 py-2 text-[12px] leading-relaxed text-muted-foreground/90">
        <span className="text-foreground/90">Next step · </span>{row.nextStep}
      </p>
    </DrilldownShell>
  );
}

/* -----------------------------------------------------------
 * 3. Evidence drilldown (Primary Signal)
 * ----------------------------------------------------------- */

export function EvidenceDrilldown({
  priority,
  analytics,
  trigger,
}: {
  priority: ParentPriority;
  analytics: ParentAnalyticsResult;
  trigger: ReactNode;
}) {
  const stats = useMemo(() => {
    if (priority.module === "general") return null;
    return analytics.betaModuleStats[priority.module];
  }, [analytics.betaModuleStats, priority.module]);

  const recent = useMemo(() => {
    const key = priority.module === "general" ? null : priority.module;
    const allEvents = analytics.recentActivity.slice().sort((a, b) => (a.at > b.at ? -1 : 1));
    if (!key) return allEvents.slice(0, 12);
    const matchers = MODULE_MATCHERS[key] ?? [];
    const filtered = allEvents.filter((ev) => {
      const hay = `${ev.title ?? ""} ${ev.topic ?? ""} ${ev.detail ?? ""}`.toLowerCase();
      return matchers.some((kw) => hay.includes(kw));
    });
    return (filtered.length > 0 ? filtered : allEvents).slice(0, 12);
  }, [analytics.recentActivity, priority.module]);

  return (
    <DrilldownShell
      trigger={trigger}
      eyebrow="Evidence"
      title={priority.concept === "general" ? "What we're seeing" : `Why ${priority.label} is the focus`}
      subtitle={priority.evidence}
      accent="cyan"
      icon={Radar}
    >
      {priority.betaIndex != null && (
        <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
          <SectionTitle accent="cyan">Beta path position</SectionTitle>
          <div className="mt-2">
            <StatRow label="Step" value={`${priority.betaIndex} of 13`} hint={priority.moduleLabel} />
          </div>
        </section>
      )}

      {stats && (
        <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
          <SectionTitle accent="cyan">Underlying module stats</SectionTitle>
          <div className="mt-2">
            <StatRow label="Lessons completed" value={`${stats.lessonsCompleted} / ${stats.lessonsTotal}`} />
            <StatRow
              label="Quiz average"
              value={stats.quizCount > 0 ? `${Math.round(stats.averageQuizScore)}%` : "—"}
              hint={stats.quizCount > 0 ? `${stats.quizCount} attempt${stats.quizCount === 1 ? "" : "s"}` : "No quizzes yet"}
            />
            <StatRow
              label="Last activity"
              value={stats.lastActivityAt ? relWhen(stats.lastActivityAt) : "—"}
              hint={stats.lastActivityAt ? formatStamp(stats.lastActivityAt) : undefined}
            />
          </div>
        </section>
      )}

      <section>
        <SectionTitle accent="cyan">Recent events feeding this signal</SectionTitle>
        <div className="mt-2 space-y-1.5">
          {recent.length === 0 ? (
            <EmptyNote>Not enough activity has been recorded to anchor this signal yet.</EmptyNote>
          ) : (
            recent.map((ev) => (
              <div key={ev.id} className="flex items-start gap-2.5 rounded-xl border border-white/[0.04] bg-background/30 px-3 py-2">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-medium text-foreground">{ev.title}</p>
                  <p className="truncate text-[11px] text-muted-foreground/85">{ev.detail}</p>
                </div>
                <div className="shrink-0 text-right">
                  {ev.score != null && (
                    <p className="font-heading text-[12.5px] font-semibold text-foreground">{ev.score}%</p>
                  )}
                  <p className="text-[10.5px] text-muted-foreground/80" title={formatStamp(ev.at)}>{relWhen(ev.at)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <p className="flex items-start gap-2 rounded-xl border border-white/[0.05] bg-white/[0.02] px-3 py-2 text-[12px] leading-relaxed text-muted-foreground/90">
        <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-300" />
        <span><span className="text-foreground/90">Watch · </span>{priority.watch}</span>
      </p>
    </DrilldownShell>
  );
}